import React from 'react';
import { ArrowUpRight, Clock, AlertTriangle, Banknote, CheckCircle } from 'lucide-react';
import { formatCurrencyAmount, formatPercentageValue } from '../../../../../utils/miscFunctions';
import { useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../../states/slices/resourceSlice';

export interface ISettlementOverviewMetrics {
  currency: string;
  expectedCount: number;
  expectedGross: number;
  expectedNet: number;
  expectedWHT: number;

  receivedCount: number;
  receivedGross: number;
  receivedNet: number;
  receivedWHT: number;

  totalCount: number;
  totalGross: number;
  totalNet: number;
  totalWHT: number;
}



interface SettlementsOverviewPanelProps {
  data: ISettlementOverviewMetrics;
  isLoading: boolean;
  onCardClick?: (isPaid: boolean | null) => void;
}

const SettlementsOverviewPanel: React.FC<SettlementsOverviewPanelProps> = ({ data, isLoading, onCardClick }) => {

  // Calculate percentages safely (avoid division by zero)
  const totalAmount = data?.totalGross || 1;
  const receivedPercentage = (data?.receivedNet / totalAmount) * 100;
  const pendingPercentage = (data?.totalNet / totalAmount) * 100;
  const selectedCountry = useSelector(getSelectedCountry);
  const currency = data?.currency || 'GHS';

  // Update the statistics array in the component
  const statistics = [
    {
      title: 'Total',
      count: data?.totalCount,
      amount: data?.totalNet,
      gross: data?.totalGross,
      WHT: data?.totalWHT,
      borderColor: 'border-blue-500 dark:border-blue-400',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      textColor: 'text-blue-600 dark:text-blue-300',
      icon: <Banknote className="h-6 w-6 text-blue-600 dark:text-blue-300" />,
      filterValue: null
    },
    {
      title: 'Settled',
      count: data?.receivedCount,
      amount: data?.receivedNet,
      gross: data?.receivedGross,
      WHT: data?.receivedWHT,
      borderColor: 'border-green-500 dark:border-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      textColor: 'text-green-600 dark:text-green-300',
      icon: <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />,
      filterValue: true
    },
    {
      title: 'Pending',
      count: data?.expectedCount,
      amount: data?.expectedNet,
      gross: data?.expectedGross,
      WHT: data?.expectedWHT,
      pendingPercentage: pendingPercentage,
      borderColor: 'border-yellow-500 dark:border-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      textColor: 'text-yellow-600 dark:text-yellow-300',
      icon: <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-300" />,
      filterValue: false
    }
  ];

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-darkBg rounded-lg shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
        <div className="h-8 bg-gray-200 dark:bg-darkBg-border rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-white dark:bg-darkBg rounded-lg p-4 shadow border-l-4 border-gray-200 dark:border-darkBg-border">
              <SkeletonLoader />
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-darkBg rounded-lg shadow-sm border border-gray-200 dark:border-darkBg-border p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText mb-4">Overview</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 my-6">
        {statistics.map((statistic) => (
          <div key={statistic.title} className={`bg-white dark:bg-darkBg cursor-pointer rounded-lg p-4 shadow border-l-4 dark:border-t dark:border-b dark:border-r dark:border-t-darkBg-border dark:border-b-darkBg-border dark:border-r-darkBg-border  ${statistic.borderColor}`} onClick={() => onCardClick?.(statistic.filterValue)}>
            <div className="flex flex-wrap flex-col-reverse items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500 dark:text-darkBg-subText">{statistic.title}{` (${currency})`}</p>
                <p className="text-2xl font-bold text-gray-800 dark:text-darkBg-mainText">
                  {formatCurrencyAmount(statistic.amount, undefined, false)}
                </p>

                <div className="text-xs text-gray-500 dark:text-darkBg-subText">
                  Gross: {formatCurrencyAmount(statistic.gross, undefined, false)}
                </div>

                <div className="text-xs text-gray-500 dark:text-darkBg-subText">
                  WHT: {formatCurrencyAmount(statistic.WHT, undefined, false)}
                </div>

                <div className="text-xs text-gray-500 dark:text-darkBg-subText mt-1">
                  {statistic.count} {statistic.count === 1 ? 'settlement' : 'settlements'} • {
                    statistic.title === 'Total Payouts'
                      ? '100% of total'
                      : `${formatPercentageValue((statistic.amount / totalAmount) * 100, undefined, true)} of total`
                  }
                </div>
              </div>
              <div className={`rounded-full ${statistic.bgColor} p-3`}>
                {statistic.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <div className="text-sm font-medium text-gray-700 dark:text-darkBg-subText">
          <span className="text-green-600 dark:text-green-400">{formatPercentageValue(receivedPercentage)}%</span> of payouts already settled
        </div>
      </div>
    </div>
  );
};

export default SettlementsOverviewPanel;

const SkeletonLoader = () => (
  <div className="space-y-4">
    <div className="h-6 bg-gray-200 dark:bg-darkBg-border rounded w-1/3"></div>
    <div className="h-10 bg-gray-200 dark:bg-darkBg-border rounded w-2/3"></div>
    <div className="h-4 bg-gray-200 dark:bg-darkBg-border rounded w-1/2"></div>
  </div>
);